import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Pause, Play, CheckCircle2, AlertCircle, AlertTriangle, Loader2, Phone, RotateCw, Pencil, X, Check, Upload, Download, Plus, Trash2 } from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { campaignApi } from '../api/outboundCampaign.js';
import { tenantsApi } from '../api/tenants.js';
import { accountsApi } from '../api/accounts.js';
import { api, download } from '../api/client.js';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { QuotaModal } from '../components/MinuteQuota.jsx';
import { overlayDismiss } from '../lib/overlayDismiss';
import { TrunkCallerIdFields, describeTrunkCallerId } from '../components/Outbound/TrunkCallerIdFields.jsx';

// daysOfWeek 一律用 JS getDay() 語意（0=週日 … 6=週六）。外部 API 建的活動可能送 ISO 週次
// （1=週一 … 7=週日）→ 7 落在查表範圍外，直接 map 會拿到 undefined 讓整頁 crash。
// 後端已在寫入/讀取時 normalize，這裡再擋一層，讓歷史 doc 也不會白頁。
const DAY_KEYS = ['day.sun', 'day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat'];
const normalizeDays = (days) => {
    if (!Array.isArray(days)) return [];
    const out = new Set();
    for (const d of days) {
        const n = Number(d);
        if (!Number.isInteger(n)) continue;
        if (n === 7) out.add(0);
        else if (n >= 0 && n <= 6) out.add(n);
    }
    return [...out].sort((a, b) => a - b);
};

export default function OutboundCampaignDetailPage() {
    const { t } = useTranslation('outbound');
    // 對應後端 tenantGuard：viewer 唯讀；admin / agent 可操作；刪活動 / 刪名單限 tenant admin
    const { hostId, isAdmin, canOperate } = useTenant();
    const { campaignId } = useParams();
    const nav = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [editing, setEditing] = useState(false);
    const [editDraft, setEditDraft] = useState(null);
    // 分類設定預覽（名單哪一欄當分類 / 值分布 / 開場白覆蓋率）；catTry = 編輯中換欄位的即時試算
    const [catPreview, setCatPreview] = useState(null);
    const [catTry, setCatTry] = useState(null);
    const [catPreviewNonce, setCatPreviewNonce] = useState(0);   // 存檔後強制重抓（avatar 層改動不會反映在 campaign 欄位上）
    const [newCatName, setNewCatName] = useState('');
    const [importJob, setImportJob] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [retryDialogOpen, setRetryDialogOpen] = useState(false);
    // SIP Trunk 模式：併發上限 = trunk.channels（0 = 分機模式）
    const [trunkChannels, setTrunkChannels] = useState(0);
    const [trunkInfo, setTrunkInfo] = useState(null);   // SIP Trunk 顯號設定 { callerIdMode, callerId, dids[] }

    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        tenantsApi.get(hostId).then((r) => {
            if (cancelled) return;
            const tn = r?.tenant;
            setTrunkChannels(tn?.pbx?.mode === 'trunk' ? Math.max(1, Number(tn?.trunk?.channels) || 1) : 0);
            setTrunkInfo(tn?.pbx?.mode === 'trunk' ? {
                callerIdMode: tn?.trunk?.callerIdMode || 'pbx',
                callerId: tn?.trunk?.callerId || '',
                dids: Array.isArray(tn?.trunk?.dids) ? tn.trunk.dids.map(String).filter(Boolean) : [],
            } : null);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [hostId]);
    const [addOpen, setAddOpen] = useState(false);
    const [schema, setSchema] = useState(null);   // { fields, phoneCount, categoryOptions }
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [exportDate, setExportDate] = useState('');   // 每日統計匯出：單一天（YYYY-MM-DD，台北時區）；留空=全部
    const [exporting, setExporting] = useState('');   // '' | 'csv' | 'xlsx'
    const PAGE_SIZE = 50;
    const [page, setPage] = useState(1);   // 名單頁碼分頁（1-based）

    const toggleSelect = (id) => setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleSelectAll = () => setSelectedIds(s => s.size === contacts.length && contacts.length > 0 ? new Set() : new Set(contacts.map(c => c.id)));
    const bulkDelete = async () => {
        const ids = [...selectedIds];
        if (ids.length === 0) return;
        if (!confirm(t('detail.bulk.confirm', { n: ids.length }))) return;
        setBulkBusy(true);
        try {
            await campaignApi.bulkDeleteContacts(hostId, campaignId, ids);
            setSelectedIds(new Set());
            await reload();
            showToast(t('detail.toast.deletedN', { n: ids.length }));
        } catch (e) { showToast(e.message || t('detail.toast.deleteFail'), 'warn'); }
        finally { setBulkBusy(false); }
    };
    const deleteOne = async (c) => {
        const nm = c.variables?.name || c.sourceRow?.['姓名'] || t('detail.deleteOne.fallbackName');
        if (!confirm(t('detail.deleteOne.confirm', { name: nm }))) return;
        try {
            await campaignApi.deleteContact(hostId, campaignId, c.id);
            setSelectedIds(s => { const n = new Set(s); n.delete(c.id); return n; });
            await reload();
            showToast(t('detail.toast.deleted'));
        } catch (e) { showToast(e.message || t('detail.toast.deleteFail'), 'warn'); }
    };
    const [toast, setToast] = useState(null);   // { msg, kind: 'success' | 'info' | 'warn' }
    const fileInputRef = useRef(null);
    // 開場白：avatar firstMessage（活動沒設 greetingTemplate 時的實際 fallback，用來顯示「目前開場白」）
    const [avatarFirstMessage, setAvatarFirstMessage] = useState(null);
    // 開場白：直接編輯文字
    const [greetTextOpen, setGreetTextOpen] = useState(false);
    const [greetTextDraft, setGreetTextDraft] = useState('');
    const [greetSaving, setGreetSaving] = useState(false);
    // 開場白：匯入流程文件重新生成（背景 job，不卡 UI）— 先選檔+填指示，按「開始生成」才送出
    const [greetDocOpen, setGreetDocOpen] = useState(false);
    const [greetInstruction, setGreetInstruction] = useState('');
    const [greetDocFile, setGreetDocFile] = useState(null);
    const [greetUploading, setGreetUploading] = useState(false);
    const greetFileInputRef = useRef(null);

    const [quotaMsg, setQuotaMsg] = useState(null);   // 分鐘數用完的提示視窗
    const showToast = (msg, kind = 'success') => {
        setToast({ msg, kind });
        setTimeout(() => setToast(null), 4000);
    };

    // 名單快照 CSV（每筆聯絡人一列）— 用 download() 帶 token，避免 <a href download> 401
    const handleExportCsv = async () => {
        if (exporting) return;
        setExporting('csv');
        try {
            await download(campaignApi.exportCsvUrl(hostId, campaignId));
        } catch (e) {
            showToast(e.message || t('detail.list.exportFail'), 'warn');
        } finally { setExporting(''); }
    };

    // 每日撥打統計 xlsx（每日彙總 + 通話明細）— 選單一天（留空=全部）
    const handleExportDaily = async () => {
        if (exporting) return;
        setExporting('xlsx');
        try {
            // 選了某天 → from=to=那天（只匯那天）；留空 → 不帶參數（全部）
            const range = exportDate ? { from: exportDate, to: exportDate } : {};
            await download(campaignApi.exportDailyXlsxUrl(hostId, campaignId, range));
        } catch (e) {
            showToast(e.message || t('detail.list.exportFail'), 'warn');
        } finally { setExporting(''); }
    };

    const reload = async () => {
        if (!hostId || !campaignId) return;
        try {
            const [c, ct] = await Promise.all([
                campaignApi.get(hostId, campaignId),
                campaignApi.listContacts(hostId, campaignId, { status: statusFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
            ]);
            setCampaign(c.campaign);
            setContacts(ct.contacts || []);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setSelectedIds(new Set()); reload(); /* eslint-disable-next-line */ }, [hostId, campaignId, statusFilter, page]);

    // 分類設定預覽（掃整份名單的 variables，最多 5000 筆）— 不掛在 reload() 的輪詢上，
    // 只在「名單筆數 / 分類設定」真的變了才重抓；失敗不影響主畫面。
    const catPreviewKey = campaign ? JSON.stringify([campaign.stats?.totalContacts, campaign.greetingTemplates, campaign.closingTemplates, campaign.variableMapping]) : '';
    useEffect(() => {
        if (!hostId || !campaignId || !campaign) return;
        let cancelled = false;
        campaignApi.categoryPreview(hostId, campaignId)
            .then(r => { if (!cancelled) setCatPreview(r); })
            .catch(() => { if (!cancelled) setCatPreview(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId, campaignId, catPreviewKey, catPreviewNonce]);

    // 載入欄位 schema（手動新增/編輯表單用）— 名單數變動後重抓（第一筆匯入後才有 schema）
    useEffect(() => {
        if (!hostId || !campaignId) return;
        campaignApi.contactSchema(hostId, campaignId)
            .then(r => setSchema(r))
            .catch(() => setSchema(null));
    }, [hostId, campaignId, contacts.length]);

    // 抓綁定 avatar 的 firstMessage — 活動沒設 greetingTemplate 時，這就是接通後 AI 實際會講的開場白。
    // 用來在設定面板顯示「目前開場白（來自 AI Agent）」，讓用戶看得到、知道要不要覆寫。
    useEffect(() => {
        const avId = campaign?.avatarId;
        if (!hostId || !avId) { setAvatarFirstMessage(null); return; }
        let cancelled = false;
        accountsApi.tenantAvatars(hostId)
            .then(r => {
                if (cancelled) return;
                const list = r?.avatars || r || [];
                const av = Array.isArray(list) ? list.find(a => a.avatarId === avId || a.id === avId) : null;
                setAvatarFirstMessage(av?.firstMessage ?? null);
            })
            .catch(() => { if (!cancelled) setAvatarFirstMessage(null); });
        return () => { cancelled = true; };
    }, [hostId, campaign?.avatarId]);

    // 已封存 → 自動跳回列表
    useEffect(() => {
        if (campaign?.status === 'archived') nav('/outbound', { replace: true });
    }, [campaign?.status, nav]);

    // running / scheduled 自動 reload stats（scheduled 等 scheduler 升 running）
    // 動態 polling 間隔：撥打中 5s、active 但沒在撥 15s、scheduled 30s
    useEffect(() => {
        if (!['running', 'scheduled'].includes(campaign?.status)) return;
        const inProgress = campaign?.stats?.inProgress || 0;
        const interval = inProgress > 0 ? 5000
            : campaign.status === 'running' ? 15000
            : 30000;
        const t = setInterval(reload, interval);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [campaign?.status, campaign?.stats?.inProgress]);

    // 匯入中 3s poll 一次
    useEffect(() => {
        const jobId = campaign?.importJobId;
        if (!jobId) { setImportJob(null); return; }
        let cancelled = false;
        const poll = async () => {
            try {
                const r = await api('GET', `/ivr-builder/api/outbound-import/jobs/${jobId}`);
                if (cancelled) return;
                setImportJob(r.job);
                if (['done', 'error'].includes(r.job.status)) {
                    reload();   // refresh campaign stats（contacts 寫進來）
                }
            } catch (e) {
                // job not found（builder-api 重啟後 in-memory job 沒了）→ 清掉 stale importJobId，停輪詢
                if (cancelled) return;
                const msg = String(e?.message || '');
                if (/job not found|404/i.test(msg)) {
                    setImportJob(null);
                    campaignApi.update(hostId, campaignId, { importJobId: null }).catch(() => {});
                }
            }
        };
        poll();
        const t = setInterval(poll, 3000);
        return () => { cancelled = true; clearInterval(t); };
        // eslint-disable-next-line
    }, [campaign?.importJobId]);

    const handleAddContacts = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('hostId', hostId);
            fd.append('campaignId', campaignId);
            fd.append('file', file);
            fd.append('autoCommit', 'true');
            // 不 autoStart — 詳細頁的「重新匯入」/「增量匯入」用戶自己決定何時啟動
            await api('POST', '/ivr-builder/api/outbound-import/start', { body: fd });
            await reload();
        } catch (e) {
            alert(t('detail.alert.uploadFail', { msg: e.message }));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // 直接編輯開場白文字 → 寫進 campaign.greetingTemplate（留空 = 清掉，退回 avatar firstMessage / 系統預設）
    const saveGreetingText = async () => {
        setGreetSaving(true);
        try {
            await campaignApi.update(hostId, campaignId, { greetingTemplate: greetTextDraft.trim() || null });
            setGreetTextOpen(false);
            showToast(t('detail.greeting.textSaved'));
            await reload();
        } catch (e) {
            alert(t('detail.alert.saveFail', { msg: e.message }));
        } finally {
            setGreetSaving(false);
        }
    };

    // 匯入流程文件 → 背景重新生成開場白（只給 flowDoc，不給名單；不 autoStart）。
    // 沿用既有 outbound-import/start 背景 job + importJobId 輪詢，完全不卡 UI。
    // ⚠️ 只在按「開始生成」時呼叫（選檔只是記住檔案，不立即送出）。
    const handleGreetingDoc = async () => {
        if (!greetDocFile) return;
        setGreetUploading(true);
        try {
            const fd = new FormData();
            fd.append('hostId', hostId);
            fd.append('campaignId', campaignId);
            fd.append('flowDoc', greetDocFile);
            if (greetInstruction.trim()) fd.append('greetingInstruction', greetInstruction.trim());
            fd.append('autoCommit', 'true');
            fd.append('autoStart', 'false');   // 只生成開場白，不自動開撥
            await api('POST', '/ivr-builder/api/outbound-import/start', { body: fd });
            setGreetDocOpen(false);
            setGreetInstruction('');
            setGreetDocFile(null);
            showToast(t('detail.greeting.docStarted'));
            await reload();   // 帶出 importJobId → 開始輪詢進度
        } catch (e) {
            alert(t('detail.alert.uploadFail', { msg: e.message }));
        } finally {
            setGreetUploading(false);
            if (greetFileInputRef.current) greetFileInputRef.current.value = '';
        }
    };

    if (loading) return <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>;
    if (!campaign) return (
        <div className="text-sm text-danger-500">
            {error ? t('detail.loadFailWith', { msg: error.message }) : t('detail.notFound')}
        </div>
    );
    if (campaign.status === 'archived') return <div className="text-sm text-ink-500">{t('detail.archivedRedirect')}</div>;

    const stats = campaign.stats || {};
    const total = stats.totalContacts || 0;
    const progressPct = total > 0 ? Math.round(((stats.doneSuccess + stats.doneFailed) / total) * 100) : 0;
    const isRunning = campaign.status === 'running';

    // 有效並發 = scheduler 實際同時撥的通數。與後端 outboundScheduler / 列表卡片同一條公式：
    // 選了分機 → 分機數（一支 ext 同時一通）；沒選 → 退回 concurrency 欄位（殘留 fallback，預設 2）。
    // ⚠️ campaign.concurrency 這欄在「有選分機」時根本沒被 scheduler 讀 → 詳情頁不能只顯示它（會誤顯示 2）。
    const endpointCount = Array.isArray(campaign.endpoints) ? campaign.endpoints.filter(Boolean).length : 0;
    const effectiveConcurrency = endpointCount > 0 ? endpointCount : (campaign.concurrency || 2);

    // 名單頁碼分頁：依目前 status 分類算總筆數 → 推總頁數
    const FILTER_STAT_KEY = { '': 'totalContacts', pending: 'pending', in_progress: 'inProgress', retry_pending: 'retryPending', done_success: 'doneSuccess', done_failed: 'doneFailed' };
    const filteredTotal = stats[FILTER_STAT_KEY[statusFilter] || 'totalContacts'] || 0;
    const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));

    const outcomeStats = campaign.outcomeStats || {};
    const totalAttempts = Object.values(outcomeStats).reduce((a, b) => a + (b || 0), 0);
    const answered = outcomeStats.answered || 0;
    const answerRate = totalAttempts > 0 ? Math.round((answered / totalAttempts) * 100) : 0;

    const handleAction = async (action) => {
        try {
            if (action === 'pause') await campaignApi.pause(hostId, campaignId);
            else if (action === 'resume') await campaignApi.resume(hostId, campaignId);
            else if (action === 'start') {
                const r = await campaignApi.start(hostId, campaignId);
                if (r?.quotaExhausted) setQuotaMsg(t('quotaNotice'));   // 分鐘數用完：活動照開，排程會先暫停
            }
            else if (action === 'finish') {
                if (!confirm(t('detail.confirm.finish'))) return;
                await campaignApi.finish(hostId, campaignId);
            } else if (action === 'delete') {
                if (['running', 'paused', 'scheduled'].includes(campaign.status)) {
                    alert(t('detail.alert.deleteBlocked'));
                    return;
                }
                if (!confirm(t('detail.confirm.delete'))) return;
                await campaignApi.remove(hostId, campaignId);
                nav('/outbound');
                return;
            } else if (action === 'retry') {
                // done → 開選擇 dialog（用戶選「只重打失敗」或「全部重打」），實際 reset 在 dialog 內 fire
                if (total === 0) { alert(t('detail.alert.retryNoList')); return; }
                setRetryDialogOpen(true);
                return;
            } else if (action === 'retryFailed' || action === 'retryAll') {
                // dialog 選後 fire — retryFailed 只 reset done_failed/retry/in_progress；retryAll 連 done_success 一起
                const includeSuccess = action === 'retryAll';
                await campaignApi.resetRetry(hostId, campaignId, { includeSuccess });
                await campaignApi.update(hostId, campaignId, { status: 'running', startAt: Date.now() });
            } else if (action === 'dialNow') {
                // 立即撥 / 繼續撥 = 手動撥打 → 進入排程 + 立刻開撥
                // ⚠️ 手動按就是要現在撥：無視「重試冷卻」（冷卻只給自動排程的等待用）
                //    後端 dial-now 會清掉可撥名單的冷卻 + resume 成 running + 立刻 tick
                //    仍尊重 maxAttempts（done_failed 不重撥）跟撥打時段（tick 內部擋）
                if (total === 0) { alert(t('detail.alert.dialNoList')); return; }
                const dialable = (campaign.stats?.pending || 0) + (campaign.stats?.retryPending || 0);
                if (dialable === 0) {
                    alert(t('detail.alert.dialNoDialable'));
                    return;
                }
                // 撥打時段只做提醒（不擋 — 用戶可以先進排程，到時段自動撥）
                const w = campaign.windowConfig || {};
                const tz = w.timezone || 'Asia/Taipei';
                const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', hour12: false });
                const parts = fmt.formatToParts(new Date());
                const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
                const curDow = dowMap[parts.find(p => p.type === 'weekday')?.value] ?? 1;
                const curHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
                const days = Array.isArray(w.daysOfWeek) ? normalizeDays(w.daysOfWeek) : [1, 2, 3, 4, 5];
                const startHour = w.startHour ?? 9;
                const endHour = w.endHour ?? 21;
                const dayKeys = ['day.sun', 'day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat'];
                const issues = [];
                if (!days.includes(curDow)) issues.push(t('detail.window.notToday', { day: t(dayKeys[curDow]) }));
                if (curHour < startHour || curHour >= endHour) issues.push(t('detail.window.notInHours', { start: String(startHour).padStart(2, '0'), end: String(endHour).padStart(2, '0') }));
                if (issues.length > 0 && !confirm(t('detail.window.confirmDial', { issues: issues.join('、') }))) return;
                await campaignApi.dialNow(hostId, campaignId);
                if (issues.length === 0) {
                    showToast(t('detail.dialNow.toast', { n: dialable }));
                } else {
                    // 時段外：實際上一通都不會撥（排程器 tick 內部會擋），不能說「已開始撥打」。
                    // 下次開撥時間沿用既有的 nextWindowLabel（同檔 L1387，已處理今天/明天/週X、
                    // 今天已過 endHour 要推到下個允許日、以及沒有撥打日的情況）。
                    // 沒設任何撥打日時 nextWindowLabel 回「無撥打窗口」，直接塞進 {{when}} 會變成
                    // 「無撥打窗口自動開始」這種不通的句子 → 換一句並指路去設定。
                    const when = nextWindowLabel(Date.now(), campaign.windowConfig, t);
                    showToast(when === t('nextWindow.none')
                        ? t('detail.dialNow.queuedNoWindow', { n: dialable })
                        : t('detail.dialNow.queued', { n: dialable, when }), 'warn');
                }
                setTimeout(() => reload(), 1500);
            }
            reload();
        } catch (e) {
            // 分鐘數用完：後端擋下、活動不動
            if (e.code === 'quota_exhausted') setQuotaMsg(t('quotaResumeBlocked'));
            else alert(t('detail.alert.actionFail', { msg: e.message }));
        }
    };

    return (
        <div className="max-w-6xl">
            <Link to="/outbound" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-accent-700 mb-3">
                <ChevronLeft size={16} /> {t('detail.back')}
            </Link>

            <PageHeader
                eyebrow={`campaign · ${campaign.id.slice(0, 12)}`}
                title={campaign.name}
                description={t('detail.header.desc', { n: total, start: campaign.windowConfig?.startHour ?? 9, end: campaign.windowConfig?.endHour ?? 21, c: effectiveConcurrency })}
                actions={canOperate &&
                    <div className="flex items-center gap-2">
                        <input ref={fileInputRef} type="file"
                            accept=".xlsx,.xls,.csv,.docx,.pdf,.png,.jpg,.jpeg,.heic,.txt,.md"
                            hidden onChange={(e) => handleAddContacts(e.target.files?.[0])} />
                        {/* 撥打中(running)整個隱藏新增/匯入 — 要先暫停才會出現，不留灰按鈕 */}
                        {campaign.status !== 'running' && (
                            <Button variant="ghost" size="md" onClick={() => fileInputRef.current?.click()}
                                disabled={uploading || (importJob && ['parsing', 'committing'].includes(importJob.status))}>
                                <Upload size={14} /> {total > 0 ? t('detail.btn.addList') : t('detail.btn.importList')}
                            </Button>
                        )}
                        {campaign.status !== 'running' && (
                            <Button variant="ghost" size="md" onClick={() => setAddOpen(true)}>
                                <Plus size={14} /> {t('detail.btn.addOne')}
                            </Button>
                        )}
                        {campaign.status === 'draft' && (
                            <Button variant="accent" size="md"
                                onClick={() => handleAction('start')}
                                disabled={total === 0}
                                title={total === 0 ? t('detail.btn.startNeedList') : ''}>
                                <Play size={14} /> {t('detail.btn.start')}
                            </Button>
                        )}
                        {/* 立即撥/繼續撥：原則 = 「只要有待撥(pending)且沒在撥(inProgress=0)，
                            不管 scheduled/running/paused/done 都要能撥」(draft 用「啟動」)。
                            這樣 done 但有待撥(reset/新增後)不會卡死沒按鈕。paused 即使 pending=0 也給(恢復用)。*/}
                        {campaign.status !== 'draft' && campaign.status !== 'done' && total > 0
                            && (campaign.status === 'paused' || (campaign.stats?.inProgress || 0) === 0)
                            && ((campaign.stats?.pending || 0) > 0 || campaign.status === 'paused') && (
                            <Button variant="accent" size="md"
                                onClick={() => handleAction('dialNow')}
                                title={campaign.status === 'paused' ? t('detail.btn.dialNowTitleResume') : t('detail.btn.dialNowTitle')}>
                                <Phone size={14} /> {campaign.status === 'paused' ? t('detail.btn.continueDial') : t('detail.btn.dialNow')}
                            </Button>
                        )}
                        {['running', 'scheduled'].includes(campaign.status) && (
                            <Button variant="ghost" size="md" onClick={() => handleAction('pause')}>
                                <Pause size={14} /> {t('detail.btn.pause')}
                            </Button>
                        )}
                        {/* 只要有「已完成 / 失敗 / 等重試」的名單就給「重試」— 不限 status=done
                            （campaign 可能跑完了但 status 還停在 paused/running，之前只在 done 顯示會讓用戶找不到重試）*/}
                        {((campaign.stats?.doneSuccess || 0) + (campaign.stats?.doneFailed || 0) + (campaign.stats?.retryPending || 0) > 0) && (
                            <Button variant="accent" size="md"
                                onClick={() => handleAction('retry')}
                                disabled={total === 0}
                                title={total === 0 ? t('detail.btn.startNeedList') : t('detail.btn.retryTitle')}>
                                <RotateCw size={14} /> {t('detail.btn.retry')}
                            </Button>
                        )}
                    </div>
                }
            />

            {quotaMsg && <QuotaModal text={quotaMsg} onClose={() => setQuotaMsg(null)} />}

            {/* 排程因分鐘數用盡而暫停：充值後要客戶手動恢復 */}
            {campaign.status === 'paused' && campaign.pauseReason === 'quota_exhausted' && (
                <div className="mb-5 px-4 py-3 rounded-md border border-danger-500/40 bg-danger-500/10 text-sm text-ink-900" role="status">
                    <span className="font-semibold">⏸ {t('campaignStatus.quotaPaused')}</span>
                    <span className="ml-2 text-ink-700">{t('detail.quotaPaused')}</span>
                </div>
            )}

            {/* 匯入中進度 */}
            {importJob && ['parsing', 'committing'].includes(importJob.status) && (
                <Card className="mb-4 bg-cream-100/60 border-cream-300">
                    <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={14} className="animate-spin text-accent-500" />
                        <span className="text-sm font-medium text-ink-900">
                            {importJob.status === 'parsing' ? t('card.parsing') : t('card.writing')}
                        </span>
                        <span className="ml-auto font-mono text-xs text-ink-500">{importJob.progress || 0}%</span>
                    </div>
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-accent-500 transition-all" style={{ width: `${importJob.progress || 5}%` }} />
                    </div>
                    {importJob.log?.length > 0 && (
                        <div className="text-[11px] font-mono text-ink-500 max-h-20 overflow-y-auto">
                            {importJob.log.slice(-3).map((l, i) => <div key={i}>{l.msg}</div>)}
                        </div>
                    )}
                </Card>
            )}

            {/* 0 名單警告 — 只要 total=0 且沒在匯入中就顯示 */}
            {total === 0 && !(importJob && ['parsing', 'committing'].includes(importJob.status)) && (
                <Card className="mb-4 bg-danger-100/40 border-danger-500/30">
                    <div className="flex items-start gap-2">
                        <AlertCircle size={16} className="text-danger-500 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="text-sm font-medium text-danger-500">
                                {t('detail.import.zeroTitle')}
                            </div>
                            <div className="text-[11px] text-ink-700 mt-1">
                                {importJob?.status === 'done' ? (
                                    <>
                                        {t('detail.import.zeroDoneLead')}
                                        <ul className="list-disc pl-5 mt-1 space-y-0.5">
                                            <li>{t('detail.import.zeroReason1')}</li>
                                            <li>{t('detail.import.zeroReason2')}</li>
                                            <li>{t('detail.import.zeroReason3')}</li>
                                        </ul>
                                    </>
                                ) : t('detail.import.zeroDraftHint')}
                            </div>
                            <Button size="sm" variant="accent" className="mt-3" onClick={() => fileInputRef.current?.click()}>
                                <Upload size={12} /> {t('detail.btn.importList')}
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* 匯入警告（如有跳過的名單）*/}
            {campaign.importWarnings?.skippedCount > 0 && (
                <Card className="mb-4 bg-warn-100/40 border-warn-500/30">
                    <div className="flex items-start gap-2">
                        <AlertTriangle size={16} className="text-warn-500 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="text-sm font-medium text-warn-500">
                                {t('detail.warn.skippedN', { n: campaign.importWarnings.skippedCount })}
                            </div>
                            {campaign.importWarnings.skippedSample?.length > 0 && (
                                <details className="mt-1">
                                    <summary className="text-xs text-ink-700 cursor-pointer hover:underline">
                                        {t('detail.warn.viewSkipped', { n: campaign.importWarnings.skippedSample.length })}
                                    </summary>
                                    <div className="mt-2 max-h-40 overflow-y-auto bg-white border border-warn-500/20 rounded p-2 space-y-1">
                                        {campaign.importWarnings.skippedSample.map((row, i) => (
                                            <div key={i} className="text-[11px] font-mono text-ink-700">
                                                {Object.entries(row.sourceRow || {}).map(([k, v]) => `${k}=${v || t('detail.warn.empty')}`).join(' / ')}
                                                <span className="text-warn-500 ml-2">({row.reason || 'no phone'})</span>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* 統計卡 */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
                <StatCard label={t('detail.stat.total')} value={total} />
                <StatCard label={t('detail.stat.dialed')} value={Math.max(0, total - (stats.pending || 0))} color="text-ink-700" />
                <StatCard label={t('detail.stat.pending')} value={stats.pending || 0} color="text-ink-700" />
                <StatCard label={t('detail.stat.inProgress')} value={stats.inProgress || 0} color="text-warn-500" icon={isRunning && (stats.inProgress || 0) > 0 ? Loader2 : null} />
                <StatCard label={t('detail.stat.retryPending')} value={stats.retryPending || 0} color="text-accent-700" />
                <StatCard label={t('detail.stat.doneSuccess')} value={stats.doneSuccess || 0} color="text-success-500" />
                <StatCard label={t('detail.stat.doneFailed')} value={stats.doneFailed || 0} color="text-danger-500" />
            </div>

            {/* 接聽率分析 — 每次撥打依結果分桶 */}
            {totalAttempts > 0 && (
                <Card className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-ink-900">{t('detail.answerRate.title')}</div>
                        <div className="flex items-baseline gap-1">
                            <span className="font-mono text-2xl tabular-nums text-success-500">{answerRate}%</span>
                            <span className="text-xs text-ink-400">{t('detail.answerRate.frac', { a: answered, t: totalAttempts })}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <OutcomeStatCard label={t('detail.outcome.answered')} value={outcomeStats.answered || 0} color="text-success-500" />
                        <OutcomeStatCard label={t('detail.outcome.noAnswer')} value={(outcomeStats.no_answer || 0) + (outcomeStats.rejected || 0) + (outcomeStats.busy || 0)} color="text-warn-500" />
                        <OutcomeStatCard label={t('detail.outcome.voicemail')} value={outcomeStats.voicemail || 0} color="text-accent-700" />
                        <OutcomeStatCard label={t('detail.outcome.noReach')} value={outcomeStats.no_reach || 0} color="text-ink-400" />
                        <OutcomeStatCard label={t('detail.outcome.sysHangup')} value={outcomeStats.unknown || 0} color="text-ink-400" />
                    </div>
                </Card>
            )}

            {/* Progress bar */}
            <Card className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-ink-900">{t('detail.progress.title')}</div>
                    <div className="font-mono text-sm tabular-nums text-ink-700">{progressPct}%</div>
                </div>
                <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
                    <div className="h-full bg-accent-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
            </Card>

            {/* 設定總覽 + 可編輯 */}
            <Card className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-ink-900">{t('detail.settings.title')}</div>
                    {!canOperate ? null : !editing ? (
                        <Button size="sm" variant="ghost" onClick={() => {
                            // startAt 轉成 datetime-local 需要的 "YYYY-MM-DDTHH:mm"（local tz，24h）
                            const startAtLocal = (() => {
                                if (!campaign.startAt) return '';
                                const d = new Date(campaign.startAt);
                                const off = d.getTimezoneOffset() * 60000;
                                return new Date(d.getTime() - off).toISOString().slice(0, 16);
                            })();
                            setEditDraft({
                                windowConfig: { ...(campaign.windowConfig || {}) },
                                retryConfig: { ...(campaign.retryConfig || {}) },
                                callOrder: campaign.callOrder || 'pending_first',
                                concurrency: campaign.concurrency || 2,
                                greetingTemplate: campaign.greetingTemplate || '',
                                greetingTemplates: campaign.greetingTemplates ? { ...campaign.greetingTemplates } : null,
                                closingTemplates: campaign.closingTemplates ? { ...campaign.closingTemplates } : null,
                                // SIP Trunk 顯號 / 前置碼（分機模式不會顯示、也不送）
                                ...(trunkChannels > 0 ? { callerId: campaign.callerId || '', dialPrefix: campaign.dialPrefix || '' } : {}),
                                // 外播中允許轉真人 + 目標分機（活動層）
                                allowTransfer: campaign.allowTransfer === true,
                                transferExt: campaign.transferExt || '',
                                // 分類欄位：只帶活動層覆寫值；空 = 沿用 AI Agent 預設
                                categoryColumn: catPreview?.campaignColumn || '',
                                categoryScope: 'campaign',
                                startAtLocal
                            });
                            setCatTry(null);
                            setNewCatName('');
                            setEditing(true);
                        }}>
                            <Pencil size={12} /> {t('common:actions.edit')}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditDraft(null); }}>
                                <X size={12} /> {t('common:actions.cancel')}
                            </Button>
                            <Button size="sm" variant="accent" onClick={async () => {
                                try {
                                    // startAtLocal ("YYYY-MM-DDTHH:mm") → ms timestamp，其他欄位不送 startAtLocal
                                    const { startAtLocal, categoryColumn, categoryScope, ...rest } = editDraft;
                                    const payload = { ...rest };
                                    if (startAtLocal) {
                                        const ms = new Date(startAtLocal).getTime();
                                        if (Number.isFinite(ms)) payload.startAt = ms;
                                    }
                                    // 分類名稱空白的項目不存（避免 { '': '...' } 垃圾 key）
                                    for (const k of ['greetingTemplates', 'closingTemplates']) {
                                        if (payload[k]) {
                                            const clean = Object.fromEntries(Object.entries(payload[k]).filter(([cat]) => cat.trim()));
                                            payload[k] = Object.keys(clean).length ? clean : null;
                                        }
                                    }
                                    await campaignApi.update(hostId, campaignId, payload);
                                    // 分類欄位：有變才寫（scope=avatar 會寫進 AI Agent、同時清掉本活動覆寫）
                                    const col = (categoryColumn || '').trim();
                                    const prevCampaignCol = catPreview?.campaignColumn || '';
                                    const prevAvatarCol = catPreview?.avatarColumn || '';
                                    const changed = categoryScope === 'avatar'
                                        ? (col !== prevAvatarCol || !!prevCampaignCol)
                                        : (col !== prevCampaignCol);
                                    if (changed) await campaignApi.setCategoryColumn(hostId, campaignId, { column: col, scope: categoryScope });
                                    setCatPreviewNonce(n => n + 1);
                                    setEditing(false);
                                    setEditDraft(null);
                                    await reload();
                                } catch (e) {
                                    alert(t('detail.alert.saveFail', { msg: e.message }));
                                }
                            }}>
                                <Check size={12} /> {t('common:actions.save')}
                            </Button>
                        </div>
                    )}
                </div>

                {!editing ? (
                    <>
                        <SettingRow label={t('detail.row.agent')} value={<code className="font-mono text-[11px]">{campaign.avatarId}</code>} />
                        <SettingRow label={t('detail.row.days')} value={normalizeDays(campaign.windowConfig?.daysOfWeek).map(d => t('dayWeekPrefix', { d: t('wizard.dayShort.' + DAY_KEYS[d].split('.')[1]) })).join('、')} />
                        <SettingRow label={t('detail.row.window')} value={`${String(campaign.windowConfig?.startHour ?? 9).padStart(2, '0')}:00 - ${String(campaign.windowConfig?.endHour ?? 21).padStart(2, '0')}:00 (${campaign.windowConfig?.timezone || 'Asia/Taipei'})`} />
                        <SettingRow label={t('detail.row.ringRetry')} value={t('detail.row.ringRetryVal', { ring: campaign.retryConfig?.dialTimeoutSec || 25, grace: campaign.retryConfig?.voicemailGraceSec ?? 25, after: campaign.retryConfig?.retryAfterMin || 240, max: campaign.retryConfig?.maxAttempts || 2, phones: campaign.retryConfig?.phonesPerContact || 1 })} />
                        <SettingRow label={t('detail.row.order')} value={campaign.callOrder === 'failed_first' ? t('detail.order.failedFirst') : t('detail.order.pendingFirst')} />
                        <SettingRow label={t('detail.row.concurrency')} value={
                            <span>
                                {t('detail.row.concurrencyVal', { n: trunkChannels > 0 ? Math.min(effectiveConcurrency, trunkChannels) : effectiveConcurrency })}
                                {endpointCount > 0 && (
                                    <span className="ml-2 text-[11px] text-ink-400">{t('wizard.s4.endpoints')}×{endpointCount}</span>
                                )}
                                {trunkChannels > 0 && (
                                    <span className="ml-2 text-[11px] text-ink-400">{t('detail.row.trunkTag', { n: trunkChannels })}</span>
                                )}
                            </span>
                        } />
                        <SettingRow label={t('detail.row.startAt')} value={campaign.startAt ? formatZhDateTime(campaign.startAt, t) : '—'} />
                        <SettingRow label={t('detail.row.transfer')} value={
                            campaign.allowTransfer === true
                                ? (campaign.transferExt ? t('detail.row.transferOnExt', { ext: campaign.transferExt }) : t('detail.row.transferOn'))
                                : t('detail.row.transferOff')
                        } />
                        {trunkChannels > 0 && (
                            <SettingRow label={t('trunkCid.label')} value={
                                <span>
                                    <span className="tabular-nums">{describeTrunkCallerId(t, trunkInfo, campaign.callerId)}</span>
                                    {campaign.callerId && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-cream-200 text-ink-500">{t('trunkCid.fromCampaign')}</span>}
                                    {campaign.dialPrefix && <span className="ml-2 text-[11px] text-ink-400">{t('trunkCid.prefixTag', { p: campaign.dialPrefix })}</span>}
                                    {trunkInfo && trunkInfo.callerIdMode !== 'custom' && campaign.callerId && <span className="ml-2 text-[11px] text-warn-500">{t('trunkCid.hintPbx')}</span>}
                                </span>
                            } />
                        )}
                        {(catPreview && (catPreview.effectiveColumn || catPreview.values?.length > 0 || (campaign.greetingTemplates && Object.keys(campaign.greetingTemplates).length > 0))) ? (
                            <SettingRow label={t('detail.row.categoryColumn')} value={
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {catPreview.effectiveColumn
                                            ? <code className="font-mono text-[12px] px-1.5 py-0.5 rounded bg-cream-100 text-ink-900">{catPreview.effectiveColumn}</code>
                                            : <span className="text-ink-500 text-[13px]">{t('detail.category.colNone')}</span>}
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-cream-200 text-ink-500">
                                            {catPreview.source === 'campaign' ? t('detail.category.srcCampaign') : catPreview.source === 'avatar' ? t('detail.category.srcAvatar') : t('detail.category.srcDefault')}
                                        </span>
                                    </div>
                                    {catPreview.scanned > 0 && (
                                        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                                            <span className="text-ink-400">{t('detail.category.listValues', { n: catPreview.scanned })}</span>
                                            {catPreview.values.map(v => (
                                                <span key={v.value} title={v.hasGreeting ? t('detail.category.hasGreeting') : t('detail.category.noGreeting')}
                                                    className={['px-1.5 py-0.5 rounded border', v.hasGreeting ? 'border-success-500/40 bg-success-500/5 text-ink-700' : 'border-warn-500/50 bg-warn-100/40 text-warn-500'].join(' ')}>
                                                    {v.hasGreeting ? '✓' : '⚠'} {v.value} <span className="text-ink-400">×{v.count}</span>
                                                </span>
                                            ))}
                                            {catPreview.uncategorized > 0 && (
                                                <span className="px-1.5 py-0.5 rounded border border-cream-300 text-ink-500">{t('detail.category.uncategorized', { n: catPreview.uncategorized })}</span>
                                            )}
                                            {catPreview.values.some(v => !v.hasGreeting) && (
                                                <span className="text-warn-500">{t('detail.category.noGreetingNote')}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            } />
                        ) : null}
                        {campaign.greetingTemplates && Object.keys(campaign.greetingTemplates).length > 0 ? (
                            <SettingRow label={t('detail.row.categoryGreeting')} value={
                                <div className="space-y-2">
                                    {Object.entries(campaign.greetingTemplates).map(([cat, tpl]) => {
                                        const closing = campaign.closingTemplates?.[cat] || catPreview?.avatarClosingTemplates?.[cat] || '';
                                        const cnt = catPreview?.values?.find(v => v.value === cat)?.count;
                                        return (
                                            <div key={cat} className="border border-cream-200 rounded p-2 bg-cream-50">
                                                <div className="text-[11px] font-mono text-accent-700 mb-1 flex items-center gap-2">
                                                    {t('detail.row.categoryLabel', { cat })}
                                                    {cnt != null && <span className="font-sans text-ink-400">{t('detail.category.countTag', { n: cnt })}</span>}
                                                </div>
                                                <div className="italic text-ink-700 text-[13px] whitespace-pre-wrap">{tpl}</div>
                                                {closing && (
                                                    <div className="mt-1 text-[12px] text-ink-500 whitespace-pre-wrap"><span className="text-ink-400">{t('detail.category.closingLabel')}</span>{closing}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {campaign.greetingTemplate && (
                                        <div className="text-[10px] text-ink-400">{t('detail.row.fallbackNote')}</div>
                                    )}
                                </div>
                            } />
                        ) : null}
                        {/* 開場白：永遠顯示「目前接通後 AI 實際會講的開場白」 — 有設活動模板用模板，
                            沒設退回 AI Agent 的 firstMessage，兩者都空則紅字警告。再附「用流程文件生成」。 */}
                        <SettingRow
                            label={campaign.greetingTemplates ? t('detail.row.defaultGreeting') : t('detail.row.greetingTpl')}
                            value={
                                <div className="space-y-2">
                                    {campaign.greetingTemplate ? (
                                        <span className="italic text-ink-700 whitespace-pre-wrap">{campaign.greetingTemplate}</span>
                                    ) : campaign.greetingTemplates ? (
                                        <span className="text-ink-400 text-[13px]">{t('detail.greeting.noDefaultButCategory')}</span>
                                    ) : (avatarFirstMessage && avatarFirstMessage.trim()) ? (
                                        <span>
                                            <span className="italic text-ink-700 whitespace-pre-wrap">{avatarFirstMessage}</span>
                                            <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-cream-200 text-ink-500 align-middle whitespace-nowrap">{t('detail.greeting.fromAgent')}</span>
                                        </span>
                                    ) : (
                                        <span className="text-danger-500 text-[13px]">⚠ {t('detail.greeting.none')}</span>
                                    )}

                                    {canOperate && campaign.status !== 'running' && (
                                        <div className="space-y-2">
                                            {/* 兩個動作：直接改文字 / 用流程文件生成 */}
                                            <div className="flex items-center gap-4">
                                                <button onClick={() => { setGreetTextDraft(campaign.greetingTemplate || ''); setGreetTextOpen(o => !o); setGreetDocOpen(false); }}
                                                    className="text-[11px] text-accent-700 hover:underline inline-flex items-center gap-1">
                                                    <Pencil size={11} /> {t('detail.greeting.editText')}
                                                </button>
                                                <button onClick={() => { setGreetDocOpen(o => !o); setGreetTextOpen(false); }}
                                                    className="text-[11px] text-accent-700 hover:underline inline-flex items-center gap-1">
                                                    <Upload size={11} /> {t('detail.greeting.genFromDoc')}
                                                </button>
                                            </div>

                                            {/* (a) 直接編輯開場白文字 */}
                                            {greetTextOpen && (
                                                <div className="p-3 rounded-lg border border-cream-200 bg-cream-50/70 space-y-2 max-w-xl">
                                                    <textarea value={greetTextDraft} rows={3}
                                                        onChange={(e) => setGreetTextDraft(e.target.value)}
                                                        placeholder={t('detail.greeting.textPh')}
                                                        className="w-full px-2 py-1.5 text-[13px] border border-cream-300 rounded bg-white resize-none focus:outline-none focus:border-accent-500" />
                                                    <div className="text-[10px] text-ink-400">{t('detail.greeting.varHint')}</div>
                                                    <div className="flex items-center gap-2">
                                                        <Button size="sm" variant="accent" disabled={greetSaving} onClick={saveGreetingText}>
                                                            {greetSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                                            {t('common:actions.save')}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => setGreetTextOpen(false)}>
                                                            {t('common:actions.cancel')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* (b) 用流程文件生成 — 先選檔+填指示，按「開始生成」才送出 */}
                                            {greetDocOpen && (
                                                <div className="p-3 rounded-lg border border-cream-200 bg-cream-50/70 space-y-2 max-w-xl">
                                                    <div className="text-[11px] text-ink-500">{t('detail.greeting.docHint')}</div>
                                                    <div>
                                                        <div className="text-[11px] text-ink-500 mb-1">{t('detail.greeting.instructionLabel')}</div>
                                                        <textarea value={greetInstruction} rows={2}
                                                            onChange={(e) => setGreetInstruction(e.target.value)}
                                                            placeholder={t('detail.greeting.instructionPh')}
                                                            className="w-full px-2 py-1.5 text-[13px] border border-cream-300 rounded bg-white resize-none focus:outline-none focus:border-accent-500" />
                                                    </div>
                                                    <input ref={greetFileInputRef} type="file" accept=".docx,.pdf,.txt,.md"
                                                        hidden onChange={(e) => setGreetDocFile(e.target.files?.[0] || null)} />
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Button size="sm" variant="ghost" onClick={() => greetFileInputRef.current?.click()}>
                                                            <Upload size={12} /> {greetDocFile ? t('detail.greeting.changeDoc') : t('detail.greeting.pickDoc')}
                                                        </Button>
                                                        {greetDocFile
                                                            ? <span className="text-[11px] text-ink-700 truncate max-w-[220px]">{greetDocFile.name}</span>
                                                            : <span className="text-[10px] text-ink-400">{t('detail.greeting.noDocYet')}</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 pt-1 border-t border-cream-200">
                                                        <Button size="sm" variant="accent" disabled={greetUploading || !greetDocFile}
                                                            onClick={handleGreetingDoc}>
                                                            {greetUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                                            {t('detail.greeting.startGen')}
                                                        </Button>
                                                        <span className="text-[10px] text-ink-400">{t('detail.greeting.bgNote')}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            } />
                    </>
                ) : (
                    <div className="space-y-3 mt-2">
                        <SettingRow label={t('detail.row.agent')} value={<code className="font-mono text-[11px] text-ink-400">{t('detail.row.agentReadonly', { id: campaign.avatarId })}</code>} />

                        <div className="flex items-start gap-3 py-2 border-b border-cream-100">
                            <div className="text-xs text-ink-400 w-24 shrink-0 pt-1">{t('detail.row.days')}</div>
                            <div className="flex gap-1 flex-1">
                                {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((dk, i) => {
                                    const d = t('wizard.dayShort.' + dk);
                                    const on = normalizeDays(editDraft.windowConfig?.daysOfWeek).includes(i);
                                    return (
                                        <button key={i} onClick={() => setEditDraft(d => ({
                                            ...d,
                                            windowConfig: {
                                                ...d.windowConfig,
                                                // 先 normalize 再增刪：raw 可能含 ISO 的 7（=週日），
                                                // 直接 filter(x => x !== 0) 取消不掉，殘留的 7 會讓週日一直開著。
                                                daysOfWeek: on
                                                    ? normalizeDays(d.windowConfig.daysOfWeek).filter(x => x !== i)
                                                    : [...normalizeDays(d.windowConfig.daysOfWeek), i].sort((a, b) => a - b)
                                            }
                                        }))} className={[
                                            'size-7 rounded text-xs font-medium border',
                                            on ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-ink-700 border-cream-300'
                                        ].join(' ')}>{d}</button>
                                    );
                                })}
                            </div>
                        </div>

                        <EditNumRow label={t('detail.edit.startHour')} value={editDraft.windowConfig?.startHour ?? 9}
                            onChange={(v) => setEditDraft(d => ({ ...d, windowConfig: { ...d.windowConfig, startHour: v } }))} min={0} max={23} suffix=":00" />
                        <EditNumRow label={t('detail.edit.endHour')} value={editDraft.windowConfig?.endHour ?? 21}
                            onChange={(v) => setEditDraft(d => ({ ...d, windowConfig: { ...d.windowConfig, endHour: v } }))} min={1} max={24} suffix=":00" />
                        <EditNumRow label={t('detail.edit.ringTimeout')} value={editDraft.retryConfig?.dialTimeoutSec ?? 25}
                            onChange={(v) => setEditDraft(d => ({ ...d, retryConfig: { ...d.retryConfig, dialTimeoutSec: v } }))} min={10} max={60} suffix={t('detail.suffix.sec')} />
                        <EditNumRow label={t('detail.edit.voicemailGrace')} value={editDraft.retryConfig?.voicemailGraceSec ?? 25}
                            onChange={(v) => setEditDraft(d => ({ ...d, retryConfig: { ...d.retryConfig, voicemailGraceSec: v } }))} min={0} max={120} suffix={t('detail.suffix.sec')} />
                        <EditNumRow label={t('detail.edit.retryCooldown')} value={editDraft.retryConfig?.retryAfterMin ?? 240}
                            onChange={(v) => setEditDraft(d => ({ ...d, retryConfig: { ...d.retryConfig, retryAfterMin: v } }))} min={1} max={1440} suffix={t('detail.suffix.min')} />
                        <EditNumRow label={t('detail.edit.maxRetry')} value={editDraft.retryConfig?.maxAttempts ?? 2}
                            onChange={(v) => setEditDraft(d => ({ ...d, retryConfig: { ...d.retryConfig, maxAttempts: v } }))} min={0} max={5} suffix={t('detail.suffix.times')} />
                        <EditNumRow label={t('wizard.s4.phonesPerContact')} value={editDraft.retryConfig?.phonesPerContact ?? 1}
                            onChange={(v) => setEditDraft(d => ({ ...d, retryConfig: { ...d.retryConfig, phonesPerContact: v } }))} min={1} max={6} suffix={t('detail.suffix.phones')} />
                        {endpointCount > 0 ? (
                            // 有選分機 → 並發由分機數決定，scheduler 不讀 concurrency → 給唯讀顯示而非無效輸入框
                            <div className="flex items-center gap-3 py-2 border-b border-cream-100">
                                <div className="text-xs text-ink-400 w-24 shrink-0">{t('detail.edit.concurrency')}</div>
                                <span className="text-sm text-ink-700 tabular-nums">{t('detail.row.concurrencyVal', { n: endpointCount })}</span>
                                <span className="text-[11px] text-ink-400">{t('endpointPicker.desc')}</span>
                            </div>
                        ) : (
                            <EditNumRow label={t('detail.edit.concurrency')} value={editDraft.concurrency || 2}
                                onChange={(v) => setEditDraft(d => ({ ...d, concurrency: v }))} min={1}
                                max={trunkChannels > 0 ? trunkChannels : 10} suffix={t('detail.suffix.calls')} />
                        )}

                        <div className="flex items-center gap-3 py-2 border-b border-cream-100">
                            <div className="text-xs text-ink-400 w-24 shrink-0">{t('detail.edit.startAt')}</div>
                            <input type="datetime-local" value={editDraft.startAtLocal || ''}
                                onChange={(e) => setEditDraft(d => ({ ...d, startAtLocal: e.target.value }))}
                                className="px-2 py-1 text-sm border border-cream-300 rounded bg-white tabular-nums focus:outline-none focus:border-accent-500" />
                            <span className="text-[11px] text-ink-400">{t('detail.edit.startAtHint')}</span>
                        </div>

                        <div className="flex items-center gap-3 py-2 border-b border-cream-100">
                            <div className="text-xs text-ink-400 w-24 shrink-0">{t('detail.edit.order')}</div>
                            <select value={editDraft.callOrder} onChange={(e) => setEditDraft(d => ({ ...d, callOrder: e.target.value }))}
                                className="flex-1 max-w-xs px-2 py-1.5 text-sm border border-cream-300 rounded bg-white focus:outline-none focus:border-accent-500">
                                <option value="pending_first">{t('detail.order.pendingFirst')}</option>
                                <option value="failed_first">{t('detail.order.failedFirst')}</option>
                            </select>
                        </div>

                        {/* 外播中允許轉真人（活動層開關，預設關）+ 目標分機 */}
                        <div className="py-2 border-b border-cream-100 space-y-1.5">
                            <div className="flex items-start gap-3">
                                <div className="text-xs text-ink-400 w-24 shrink-0 pt-0.5">{t('detail.edit.transfer')}</div>
                                <label className="inline-flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={editDraft.allowTransfer === true}
                                        onChange={(e) => setEditDraft(d => ({ ...d, allowTransfer: e.target.checked }))}
                                        className="mt-0.5 accent-accent-500" />
                                    <span>
                                        <span className="text-sm text-ink-900">{t('wizard.s4.allowTransfer')}</span>
                                        <span className="block text-[11px] text-ink-400">{t('wizard.s4.allowTransferHint')}</span>
                                    </span>
                                </label>
                            </div>
                            {editDraft.allowTransfer === true && (
                                <div className="flex items-center gap-3">
                                    <div className="text-xs text-ink-400 w-24 shrink-0">{t('wizard.s4.transferExt')}</div>
                                    <input type="text" inputMode="numeric" value={editDraft.transferExt || ''}
                                        onChange={(e) => setEditDraft(d => ({ ...d, transferExt: e.target.value.replace(/[^0-9*#]/g, '') }))}
                                        placeholder={t('wizard.s4.transferExtPh')}
                                        className="w-40 px-2 py-1 text-sm border border-cream-300 rounded bg-white font-mono focus:outline-none focus:border-accent-500" />
                                    <span className="text-[11px] text-ink-400">{trunkChannels > 0 ? t('wizard.s4.transferExtHintTrunk') : t('wizard.s4.transferExtHint')}</span>
                                </div>
                            )}
                        </div>

                        {trunkChannels > 0 && (
                            <TrunkCallerIdFields dense trunkInfo={trunkInfo}
                                callerId={editDraft.callerId || ''} onCallerId={(v) => setEditDraft(d => ({ ...d, callerId: v }))}
                                dialPrefix={editDraft.dialPrefix || ''} onDialPrefix={(v) => setEditDraft(d => ({ ...d, dialPrefix: v }))} />
                        )}
                        {/* 分類欄位：名單哪一欄決定分類（空 = 沿用 AI Agent 預設）；換欄位即時試算值分布 */}
                        {(() => {
                            const pv = catTry || catPreview;
                            const cols = pv?.columns || [];
                            const gts = editDraft.greetingTemplates || {};
                            const undefinedVals = (pv?.values || []).filter(v => !(v.value in gts));
                            return (
                                <div className="py-2 border-b border-cream-100 space-y-2">
                                    <div className="flex items-start gap-3">
                                        <div className="text-xs text-ink-400 w-24 shrink-0 pt-1.5">{t('detail.edit.categoryColumn')}</div>
                                        <div className="flex-1 space-y-1.5">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <input list="cat-col-options" value={editDraft.categoryColumn || ''}
                                                    placeholder={catPreview?.avatarColumn ? t('detail.edit.categoryColumnPhAvatar', { col: catPreview.avatarColumn }) : t('detail.edit.categoryColumnPh')}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setEditDraft(d => ({ ...d, categoryColumn: v }));
                                                        campaignApi.categoryPreview(hostId, campaignId, { column: v.trim() }).then(setCatTry).catch(() => {});
                                                    }}
                                                    className="w-64 px-2 py-1.5 text-sm border border-cream-300 rounded bg-white focus:outline-none focus:border-accent-500" />
                                                <datalist id="cat-col-options">
                                                    {cols.map(c => <option key={c.key} value={c.key}>{`${c.key}（${c.samples.join(' / ')}）`}</option>)}
                                                </datalist>
                                                <label className="inline-flex items-center gap-1 text-[11px] text-ink-500">
                                                    <input type="radio" name="catScope" checked={editDraft.categoryScope !== 'avatar'} onChange={() => setEditDraft(d => ({ ...d, categoryScope: 'campaign' }))} />
                                                    {t('detail.edit.categoryScopeCampaign')}
                                                </label>
                                                <label className="inline-flex items-center gap-1 text-[11px] text-ink-500">
                                                    <input type="radio" name="catScope" checked={editDraft.categoryScope === 'avatar'} onChange={() => setEditDraft(d => ({ ...d, categoryScope: 'avatar' }))} />
                                                    {t('detail.edit.categoryScopeAvatar')}
                                                </label>
                                            </div>
                                            <div className="text-[10px] text-ink-400">{t('detail.edit.categoryColumnHint')}</div>
                                            {pv && pv.scanned > 0 && (
                                                <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                                                    <span className="text-ink-400">{t('detail.category.listValues', { n: pv.scanned })}</span>
                                                    {pv.values.map(v => {
                                                        const has = v.value in gts && String(gts[v.value] || '').trim();
                                                        return (
                                                            <span key={v.value} className={['px-1.5 py-0.5 rounded border', has ? 'border-success-500/40 bg-success-500/5 text-ink-700' : 'border-warn-500/50 bg-warn-100/40 text-warn-500'].join(' ')}>
                                                                {has ? '✓' : '⚠'} {v.value} <span className="text-ink-400">×{v.count}</span>
                                                            </span>
                                                        );
                                                    })}
                                                    {pv.uncategorized > 0 && <span className="px-1.5 py-0.5 rounded border border-cream-300 text-ink-500">{t('detail.category.uncategorized', { n: pv.uncategorized })}</span>}
                                                    {pv.values.length === 0 && <span className="text-warn-500">{t('detail.category.noValues')}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 分類 → 開場白 / 結尾語 */}
                                    <div className="flex items-start gap-3">
                                        <div className="text-xs text-ink-400 w-24 shrink-0 pt-1.5">{t('detail.edit.categoryGreetings')}</div>
                                        <div className="flex-1 space-y-2">
                                            <div className="text-[10px] text-ink-400">{t('detail.edit.categoryGreetingNote2')}</div>
                                            {Object.entries(gts).map(([cat, tpl]) => (
                                                <div key={cat} className="border border-cream-200 rounded p-2 bg-cream-50 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[11px] font-mono text-accent-700 flex-1 truncate">{t('detail.row.categoryLabel', { cat })}</span>
                                                        {pv?.values?.find(v => v.value === cat)
                                                            ? <span className="text-[10px] text-ink-400">{t('detail.category.countTag', { n: pv.values.find(v => v.value === cat).count })}</span>
                                                            : pv?.scanned > 0 && <span className="text-[10px] text-warn-500">{t('detail.category.notInList')}</span>}
                                                        <button type="button" title={t('detail.greeting.deleteCategory', { cat })}
                                                            onClick={() => {
                                                                if (!confirm(t('detail.greeting.deleteCategoryConfirm', { cat }))) return;
                                                                setEditDraft(d => {
                                                                    const g = { ...(d.greetingTemplates || {}) }; delete g[cat];
                                                                    const c = { ...(d.closingTemplates || {}) }; delete c[cat];
                                                                    return { ...d, greetingTemplates: Object.keys(g).length ? g : null, closingTemplates: Object.keys(c).length ? c : null };
                                                                });
                                                            }}
                                                            className="shrink-0 size-6 inline-flex items-center justify-center rounded border border-danger-300 text-danger-500 hover:bg-danger-100/40">
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                    <textarea value={tpl || ''} rows={2} placeholder={t('detail.edit.categoryGreetingPh')}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, greetingTemplates: { ...(d.greetingTemplates || {}), [cat]: e.target.value } }))}
                                                        className="w-full px-2 py-1.5 text-sm border border-cream-300 rounded bg-white resize-none focus:outline-none focus:border-accent-500" />
                                                    <textarea value={editDraft.closingTemplates?.[cat] || ''} rows={1} placeholder={t('detail.edit.categoryClosingPh')}
                                                        onChange={(e) => setEditDraft(d => {
                                                            const c = { ...(d.closingTemplates || {}) };
                                                            if (e.target.value) c[cat] = e.target.value; else delete c[cat];
                                                            return { ...d, closingTemplates: Object.keys(c).length ? c : null };
                                                        })}
                                                        className="w-full px-2 py-1 text-[12px] border border-cream-200 rounded bg-white resize-none focus:outline-none focus:border-accent-500" />
                                                </div>
                                            ))}
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <input list="cat-new-options" value={newCatName} placeholder={t('detail.edit.newCategoryPh')}
                                                    onChange={(e) => setNewCatName(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.nextElementSibling?.nextElementSibling?.click(); } }}
                                                    className="w-48 px-2 py-1.5 text-sm border border-cream-300 rounded bg-white focus:outline-none focus:border-accent-500" />
                                                <datalist id="cat-new-options">{undefinedVals.map(v => <option key={v.value} value={v.value} />)}</datalist>
                                                <button type="button" disabled={!newCatName.trim() || newCatName.trim() in gts}
                                                    onClick={() => {
                                                        const cat = newCatName.trim(); if (!cat) return;
                                                        setEditDraft(d => ({ ...d, greetingTemplates: { ...(d.greetingTemplates || {}), [cat]: '' } }));
                                                        setNewCatName('');
                                                    }}
                                                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-accent-500 text-accent-700 hover:bg-accent-500/5 disabled:opacity-40">
                                                    <Plus size={12} /> {t('detail.edit.addCategory')}
                                                </button>
                                                {undefinedVals.length > 0 && (
                                                    <span className="text-[11px] text-ink-400 inline-flex items-center gap-1 flex-wrap">
                                                        {t('detail.edit.quickAdd')}
                                                        {undefinedVals.slice(0, 8).map(v => (
                                                            <button key={v.value} type="button"
                                                                onClick={() => setEditDraft(d => ({ ...d, greetingTemplates: { ...(d.greetingTemplates || {}), [v.value]: '' } }))}
                                                                className="px-1.5 py-0.5 rounded border border-warn-500/50 text-warn-500 hover:bg-warn-100/40">+ {v.value}</button>
                                                        ))}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="flex items-start gap-3 py-2">
                            <div className="text-xs text-ink-400 w-24 shrink-0 pt-1">
                                {editDraft.greetingTemplates ? t('detail.edit.defaultGreeting') : t('detail.edit.greeting')}
                            </div>
                            <div className="flex-1 space-y-1">
                                <textarea value={editDraft.greetingTemplate || ''} rows={3}
                                    onChange={(e) => setEditDraft(d => ({ ...d, greetingTemplate: e.target.value }))}
                                    className="w-full px-2 py-1.5 text-sm border border-cream-300 rounded bg-white resize-none focus:outline-none focus:border-accent-500" />
                                <div className="text-[10px] text-ink-400">
                                    {(avatarFirstMessage && avatarFirstMessage.trim())
                                        ? t('detail.edit.greetingFallbackHint', { msg: avatarFirstMessage })
                                        : t('detail.edit.greetingFallbackNone')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* 名單表 */}
            <Card padded={false}>
                <div className="px-4 py-3 border-b border-cream-200 flex items-center justify-between">
                    <div>
                        <h3 className="heading-display text-base font-semibold text-ink-900">{t('detail.list.title')}</h3>
                        <div className="text-[11px] text-ink-400 mt-0.5">{t('detail.list.subtitle')}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && selectedIds.size > 0 && (
                            <button onClick={bulkDelete} disabled={bulkBusy}
                                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-danger-300 text-danger-500 hover:bg-danger-100/40 disabled:opacity-50">
                                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} {t('detail.bulk.deleteSelected', { n: selectedIds.size })}
                            </button>
                        )}
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                            className="text-xs px-2 py-1.5 border border-cream-300 rounded bg-white">
                            <option value="">{t('detail.filter.all')}</option>
                            <option value="pending">{t('detail.filter.pending')}</option>
                            <option value="in_progress">{t('detail.filter.inProgress')}</option>
                            <option value="retry_pending">{t('detail.filter.retryPending')}</option>
                            <option value="done_success">{t('detail.filter.doneSuccess')}</option>
                            <option value="done_failed">{t('detail.filter.doneFailed')}</option>
                        </select>
                        <Button variant="ghost" size="sm" onClick={reload}>
                            <RotateCw size={12} /> {t('common:actions.refresh')}
                        </Button>
                        <button type="button" onClick={handleExportCsv} disabled={!!exporting}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-cream-300 rounded bg-white hover:bg-cream-100 text-ink-700 disabled:opacity-50">
                            {exporting === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {t('detail.list.downloadCsv')}
                        </button>
                    </div>
                </div>
                {/* 每日撥打統計匯出：日期範圍 + Excel（每日彙總 + 通話明細兩張表） */}
                <div className="flex items-center gap-2 flex-wrap mb-4 text-xs text-ink-600 bg-cream-50 border border-cream-200 rounded px-3 py-2">
                    <span className="font-medium">{t('detail.list.dailyStats')}</span>
                    <input type="date" value={exportDate} onChange={(e) => setExportDate(e.target.value)}
                        className="px-2 py-1 border border-cream-300 rounded bg-white" />
                    {exportDate && (
                        <button type="button" onClick={() => setExportDate('')}
                            className="text-ink-400 hover:text-ink-600" title={t('detail.list.dailyStatsClear')}>
                            <X size={14} />
                        </button>
                    )}
                    <button type="button" onClick={handleExportDaily} disabled={!!exporting}
                        className="inline-flex items-center gap-1 px-3 py-1 border border-cream-300 rounded bg-white hover:bg-cream-100 text-ink-700 disabled:opacity-50">
                        {exporting === 'xlsx' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {t('detail.list.downloadDailyXlsx')}
                    </button>
                    <span className="text-ink-400">{t('detail.list.dailyStatsHint')}</span>
                </div>
                {contacts.length === 0 ? (
                    <div className="text-center text-ink-400 text-sm py-12">
                        {total === 0 ? (
                            <div>
                                <div className="italic mb-2">{t('detail.list.emptyNoList')}</div>
                                <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}>
                                    <Upload size={12} /> {t('detail.btn.importList')}
                                </Button>
                            </div>
                        ) : (
                            <div className="italic">{statusFilter ? t('detail.list.emptyFilteredWith', { filter: statusFilter }) : t('detail.list.emptyFiltered')}</div>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-cream-200 text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                                    <th className="px-3 py-2 w-8">
                                        {isAdmin && <input type="checkbox" className="align-middle"
                                            checked={contacts.length > 0 && selectedIds.size === contacts.length}
                                            ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < contacts.length; }}
                                            onChange={toggleSelectAll} />}
                                    </th>
                                    <th className="text-left px-3 py-2">{t('detail.col.name')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.category')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.currentPhone')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.attempts')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.status')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.lastResult')}</th>
                                    <th className="text-left px-3 py-2">{t('detail.col.nextDial')}</th>
                                    <th className="text-right px-3 py-2">{t('detail.col.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map(c => (
                                    <ContactRow key={c.id} contact={c} categoryColumn={catPreview?.effectiveColumn}
                                        dialTimeoutSec={campaign.retryConfig?.dialTimeoutSec || 25}
                                        windowConfig={campaign.windowConfig}
                                        maxRedials={maxRedialsOf(campaign.retryConfig)}
                                        selected={selectedIds.has(c.id)}
                                        onToggle={isAdmin ? () => toggleSelect(c.id) : undefined}
                                        onDelete={isAdmin ? () => deleteOne(c) : undefined}
                                        onClick={() => setSelectedContact(c)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {pageCount > 1 && (
                    <div className="flex items-center justify-between gap-2 px-3 py-3 mt-1 border-t border-cream-200 text-xs">
                        <div className="text-ink-400 tabular-nums">
                            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredTotal)} / {filteredTotal}
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                                className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">‹</button>
                            {buildPageWindow(page, pageCount).map((p, i) => p === '…' ? (
                                <span key={`e${i}`} className="px-1 text-ink-400">…</span>
                            ) : (
                                <button key={p} onClick={() => setPage(p)}
                                    className={`min-w-[28px] px-2 py-1 rounded border tabular-nums ${p === page ? 'border-accent-500 bg-accent-500 text-white' : 'border-cream-300 bg-white hover:bg-cream-100 text-ink-700'}`}>{p}</button>
                            ))}
                            <button disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                                className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">›</button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Toast (right-bottom auto-dismiss) */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg bg-ink-900 text-white text-sm max-w-sm animate-in slide-in-from-bottom-2">
                    {toast.msg}
                </div>
            )}

            {/* Contact detail dialog */}
            {selectedContact && (
                <ContactDetailDialog
                    contact={selectedContact}
                    hostId={hostId}
                    campaignId={campaignId}
                    windowConfig={campaign.windowConfig}
                    maxRedials={maxRedialsOf(campaign.retryConfig)}
                    schema={schema}
                    canEdit={canOperate}
                    canDelete={isAdmin}
                    onClose={() => setSelectedContact(null)}
                    onChanged={(msg) => { setSelectedContact(null); reload(); if (msg) showToast(msg); }}
                />
            )}

            {/* 手動新增一筆 dialog — schema 空時 dialog 自己 fallback 顯示「姓名 + 電話」基本 input */}
            {addOpen && (
                <AddContactDialog
                    hostId={hostId}
                    campaignId={campaignId}
                    schema={schema || { fields: [] }}
                    onClose={() => setAddOpen(false)}
                    onSaved={() => { setAddOpen(false); reload(); showToast(t('detail.toast.added')); }}
                />
            )}

            {/* Retry choice dialog */}
            {retryDialogOpen && (
                <RetryChoiceDialog
                    stats={campaign.stats || {}}
                    onClose={() => setRetryDialogOpen(false)}
                    onConfirm={(mode) => {
                        setRetryDialogOpen(false);
                        handleAction(mode === 'all' ? 'retryAll' : 'retryFailed');
                    }}
                />
            )}

            <div className="mt-6 flex justify-end gap-2">
                {canOperate && ['running', 'paused', 'scheduled'].includes(campaign.status) && (
                    <Button variant="ghost" size="sm"
                        onClick={() => handleAction('finish')}
                        title={t('detail.finish.title')}>
                        <CheckCircle2 size={12} /> {t('detail.finish')}
                    </Button>
                )}
                {isAdmin && (
                    <Button variant="ghost" size="sm"
                        onClick={() => handleAction('delete')}
                        disabled={['running', 'paused', 'scheduled'].includes(campaign.status)}
                        title={['running', 'paused', 'scheduled'].includes(campaign.status) ? t('detail.deleteCampaign.blockedTitle') : ''}>
                        {t('detail.deleteCampaign')}
                    </Button>
                )}
            </div>
        </div>
    );
}

// 頁碼窗：回 [1, '…', 4, 5, 6, '…', 40] 這種陣列（current 周圍 ±2 + 頭尾）
function buildPageWindow(current, count) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, current - 2);
    const hi = Math.min(count - 1, current + 2);
    if (lo > 2) out.push('…');
    for (let p = lo; p <= hi; p++) out.push(p);
    if (hi < count - 1) out.push('…');
    out.push(count);
    return out;
}

function StatCard({ label, value, color = 'text-ink-900', icon: Icon }) {
    return (
        <Card padded={false} className="p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">{label}</div>
            <div className={`text-2xl font-mono tnum font-semibold flex items-center gap-1.5 ${color}`}>
                {Icon && <Icon size={16} className="animate-spin" />}
                {value}
            </div>
        </Card>
    );
}

function OutcomeStatCard({ label, value, color }) {
    return (
        <div className="px-3 py-2 bg-cream-50 rounded-md border border-cream-200">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-mono mb-0.5">{label}</div>
            <div className={`text-lg font-mono tabular-nums font-semibold ${color}`}>{value}</div>
        </div>
    );
}

function outcomeBadge(outcome, durationSec, wasAnswered, dialTimeoutSec = 25, success = null, terminationType = null, t) {
    // Live AI 判定非真人 → 沒接通。分兩種：代接語音信箱（曾代接/篩選）vs 忙碌語音信箱（純電信錄音）。優先於其他判斷。
    const _tt = String(terminationType || '').toUpperCase();
    if (_tt === 'SCREENING') {
        return { text: t('outcomeBadge.screening'), cls: 'bg-warn-100 text-warn-500' };
    }
    if (_tt === 'VOICEMAIL') {
        return { text: t('outcomeBadge.voicemail'), cls: 'bg-warn-100 text-warn-500' };
    }
    // ⚠️ 不用 Goertzel 的 wasAnswered 判「接通」—— 假 200 trunk 會誤判。改信後端 outcome（已用
    //    success+terminationType 算過）：outcome=answered 才是真接通(有對話 / AI 確認真人)。
    if (outcome === 'answered') return { text: t('outcomeBadge.answered'), cls: 'bg-success-100 text-success-500' };
    // 對方話中 —— 沒講到話、會重播。獨立標示，別跟「響鈴沒接」混在一起（客戶要看得出是哪一種）
    if (outcome === 'busy') return { text: t('outcomeBadge.busy'), cls: 'bg-warn-100 text-warn-500' };
    if (outcome === 'no_answer' || outcome === 'rejected') {
        if (durationSec == null) return { text: t('outcomeBadge.noAnswer'), cls: 'bg-warn-100 text-warn-500' };
        // 門檻 = dialTimeoutSec − 2（25→23）：< 門檻 = 沒響多久就斷（立即掛 / 拒接）；≥ 門檻 = 響鈴久沒接
        const longThreshold = Math.max(1, (dialTimeoutSec || 25) - 2);
        if (durationSec < longThreshold) return { text: t('outcomeBadge.hangImmediate'), cls: 'bg-warn-100 text-warn-500' };
        return { text: t('outcomeBadge.ringLong'), cls: 'bg-warn-100 text-warn-500' };
    }
    if (outcome === 'no_reach') return { text: t('outcomeBadge.noReach'), cls: 'bg-cream-200 text-ink-500' };
    if (outcome === 'unknown') return { text: t('outcomeBadge.sysHangup'), cls: 'bg-cream-200 text-ink-500' };
    return null;
}

// 中文友善的日期時間格式：把 12:00 顯示成「中午」、00:00 顯示成「凌晨」、其他用「上午/下午」
// 避免 default toLocaleString 把「下午12:00」當 noon、「上午12:00」當 midnight 的曖昧
function formatZhDateTime(ms, t) {
    const d = new Date(ms);
    const y = d.getFullYear();
    const M = d.getMonth() + 1;
    const D = d.getDate();
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    let label, hour12;
    if (h === 0) { label = t('datetime.dawn'); hour12 = 12; }
    else if (h < 12) { label = t('datetime.am'); hour12 = h; }
    else if (h === 12) { label = t('datetime.noon'); hour12 = 12; }
    else { label = t('datetime.pm'); hour12 = h - 12; }
    return `${y}/${M}/${D} ${label}${hour12}:${m}:${s}`;
}

// 計算下一個「撥打時段開始時刻」label（依 windowConfig.daysOfWeek + startHour，考慮 timezone）
// 用於 pending 或 retry 時間已過但被時段限制擋住的 case
// 某個 timestamp 是否落在撥打時段內（對齊後端 outboundScheduler.isInWindowDetail：
// daysOfWeek + startHour <= hour < endHour，依 timezone 判斷）。
function isInWindow(ms, windowConfig) {
    const tz = windowConfig?.timezone || 'Asia/Taipei';
    const days = Array.isArray(windowConfig?.daysOfWeek) ? normalizeDays(windowConfig.daysOfWeek) : [1, 2, 3, 4, 5];
    const startHour = windowConfig?.startHour ?? 9;
    const endHour = windowConfig?.endHour ?? 21;
    const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', hour12: false })
        .formatToParts(new Date(ms));
    const dow = dowMap[parts.find(p => p.type === 'weekday')?.value] ?? 1;
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    return days.includes(dow) && hour >= startHour && hour < endHour;
}

function nextWindowLabel(fromMs, windowConfig, t) {
    const tz = windowConfig?.timezone || 'Asia/Taipei';
    const days = Array.isArray(windowConfig?.daysOfWeek) ? normalizeDays(windowConfig.daysOfWeek) : [1, 2, 3, 4, 5];
    const startHour = windowConfig?.startHour ?? 9;
    const endHour = windowConfig?.endHour ?? 21;
    const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const dayKeys = ['day.sun', 'day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat'];
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', hour12: false });
    const dayMs = 24 * 3600 * 1000;
    for (let i = 0; i < 14; i++) {
        const probe = new Date(fromMs + i * dayMs);
        const parts = fmt.formatToParts(probe);
        const dow = dowMap[parts.find(p => p.type === 'weekday')?.value] ?? 1;
        if (!days.includes(dow)) continue;
        const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
        if (i === 0 && hour >= startHour && hour < endHour) return t('nextWindow.now');
        if (i === 0 && hour >= endHour) continue;
        const prefix = i === 0 ? t('nextWindow.today') : (i === 1 ? t('nextWindow.tomorrow') : t(dayKeys[dow]));
        return t('nextWindow.fromHour', { prefix, hour: String(startHour).padStart(2, '0') });
    }
    return t('nextWindow.none');
}

function EditNumRow({ label, value, onChange, min, max, suffix }) {
    // 用 local string state 允許中途清空 / 部分輸入，避免「無法直接打數字一定要上下點」的 bug
    // （原本 onChange 用 Number(e.target.value) 會把空字串轉成 0 立刻寫回 controlled value）
    const [text, setText] = useState(String(value ?? ''));
    useEffect(() => { setText(String(value ?? '')); }, [value]);
    return (
        <div className="flex items-center gap-3 py-1.5 border-b border-cream-100">
            <div className="text-xs text-ink-400 w-24 shrink-0">{label}</div>
            <input type="number" value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    if (e.target.value === '') return;
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) onChange(n);
                }}
                onBlur={(e) => {
                    let n = Number(e.target.value);
                    if (!Number.isFinite(n)) n = (min ?? 0);
                    if (min != null) n = Math.max(min, n);
                    if (max != null) n = Math.min(max, n);
                    setText(String(n));
                    onChange(n);
                }}
                min={min} max={max}
                className="w-24 px-2 py-1 text-sm border border-cream-300 rounded bg-white tabular-nums focus:outline-none focus:border-accent-500" />
            {suffix && <span className="text-xs text-ink-500">{suffix}</span>}
        </div>
    );
}

function SettingRow({ label, value }) {
    return (
        <div className="flex items-start gap-3 py-2 border-b border-cream-100 last:border-b-0">
            <div className="text-xs text-ink-400 w-24 shrink-0">{label}</div>
            <div className="text-sm text-ink-900 flex-1">{value || '—'}</div>
        </div>
    );
}

// maxAttempts = 失敗後重播次數上限（不含首通），與後端 recordCallResult 同語意
function maxRedialsOf(retryConfig) {
    const n = Number(retryConfig?.maxAttempts);
    return Number.isFinite(n) && n >= 0 ? n : 2;
}
// contact.attempts 存的是總撥打次數（含首通）→ 顯示用「重播次數」= attempts - 1
const redialsOf = (c) => Math.max(0, (c.attempts || 0) - 1);

function ContactRow({ contact: c, onClick, dialTimeoutSec, windowConfig, maxRedials, selected, onToggle, onDelete, categoryColumn }) {
    const { t } = useTranslation('outbound');
    const phoneIdx = c.currentPhoneIdx || 0;
    const currentPhone = c.phones?.[phoneIdx]?.number || '—';
    const statusBadge = {
        pending: { bg: 'bg-cream-200', fg: 'text-ink-500', text: t('contactStatus.pending') },
        in_progress: { bg: 'bg-warn-100', fg: 'text-warn-500', text: t('contactStatus.inProgress') },
        retry_pending: { bg: 'bg-accent-50', fg: 'text-accent-700', text: t('contactStatus.retryPending') },
        done_success: { bg: 'bg-success-100', fg: 'text-success-500', text: t('contactStatus.doneSuccess') },
        done_failed: { bg: 'bg-danger-100/60', fg: 'text-danger-500', text: t('contactStatus.doneFailed') },
    }[c.status] || { bg: '', fg: '', text: '' };
    const { bg, fg, text } = statusBadge;
    // 下次可撥顯示邏輯：
    //   - terminal (done_success/done_failed) → —
    //   - in_progress → 撥打中…
    //   - retry_pending + nextEligibleAt 在未來 → 顯示時間，但若超過時段窗口要算下個窗口
    //   - pending 或 retry_pending 過去 → 算下個撥打窗口（依 daysOfWeek + startHour）
    const nextAt = (() => {
        if (c.status === 'in_progress') return t('row.dialing');
        if (c.status === 'done_success' || c.status === 'done_failed') return '—';
        const now = Date.now();
        const target = c.nextEligibleAt || now;
        if (target > now) {
            // 未來時間：落在時段內才顯示實際時間。
            // 若超出撥打時段（如 retry 算出 20:25 但時段 8-20），scheduler 不會在那時撥，
            // 要等下一個時段窗口開才撥 → 顯示下個窗口，避免誤導「會在時段外撥」。
            if (isInWindow(target, windowConfig)) {
                return new Date(target).toLocaleString('zh-TW', { hour12: false });
            }
            return nextWindowLabel(target, windowConfig, t);
        }
        // 已過 / 立即可撥 → 算下個時段窗口（會依 daysOfWeek 推到下個允許日的 startHour）
        return nextWindowLabel(now, windowConfig, t);
    })();

    return (
        <tr onClick={onClick} className={`border-b border-cream-100 hover:bg-cream-50 transition cursor-pointer ${selected ? 'bg-accent-50' : ''}`}>
            <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                {onToggle && <input type="checkbox" className="align-middle" checked={!!selected} onChange={() => onToggle()} />}
            </td>
            <td className="px-3 py-2 text-ink-900">{c.variables?.name || c.sourceRow?.['姓名'] || '—'}</td>
            <td className="px-3 py-2 text-ink-500 text-xs">{c.variables?.category || (categoryColumn && (c.variables?.[categoryColumn] ?? c.variables?.[Object.keys(c.variables || {}).find(k => k.includes(categoryColumn)) || ''])) || c.sourceRow?.['分類'] || '—'}</td>
            <td className="px-3 py-2 font-mono text-xs tabular-nums">
                <span className="text-accent-700">{currentPhone}</span>
                <span className="text-ink-400 ml-1">({phoneIdx + 1}/{(c.phones || []).length})</span>
            </td>
            <td className="px-3 py-2 font-mono text-xs tabular-nums">{redialsOf(c)}<span className="text-ink-400">/{maxRedials}</span></td>
            <td className="px-3 py-2">
                <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${bg} ${fg}`}>{text}</span>
            </td>
            <td className="px-3 py-2">
                {(() => {
                    const badge = c.lastOutcome ? outcomeBadge(c.lastOutcome, c.lastCallDurationSec, c.lastWasAnswered, dialTimeoutSec, c.lastSuccess, c.lastTerminationType, t) : null;
                    return badge ? (
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${badge.cls}`}>{badge.text}</span>
                    ) : <span className="text-ink-300 text-xs">—</span>;
                })()}
            </td>
            <td className="px-3 py-2 font-mono text-[10px] text-ink-500">{nextAt}</td>
            <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                {onDelete && (
                    <button onClick={() => onDelete()} title={t('row.deleteTitle')}
                        className="size-7 inline-flex items-center justify-center text-ink-400 hover:text-danger-500 hover:bg-cream-100 rounded transition">
                        <Trash2 size={14} />
                    </button>
                )}
            </td>
        </tr>
    );
}

function RetryChoiceDialog({ stats, onClose, onConfirm }) {
    const { t } = useTranslation('outbound');
    const succ = stats.doneSuccess || 0;
    const failed = stats.doneFailed || 0;
    const retry = stats.retryPending || 0;
    const failedTotal = failed + retry;
    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.3)] w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-cream-200 bg-white">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">retry</div>
                    <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">{t('retryDialog.title')}</h2>
                </div>
                <div className="px-6 py-5 space-y-3">
                    <button
                        onClick={() => onConfirm('failed')}
                        disabled={failedTotal === 0}
                        className="w-full text-left px-4 py-3 rounded-lg border border-cream-300 hover:border-accent-500 hover:bg-cream-100 disabled:opacity-40 disabled:cursor-not-allowed transition">
                        <div className="font-medium text-ink-900">{t('retryDialog.failedOnly')}</div>
                        <div className="text-xs text-ink-500 mt-1">
                            {t('retryDialog.failedDesc', { n: failedTotal, f: failed, r: retry })}
                        </div>
                    </button>
                    <button
                        onClick={() => onConfirm('all')}
                        className="w-full text-left px-4 py-3 rounded-lg border border-cream-300 hover:border-accent-500 hover:bg-cream-100 transition">
                        <div className="font-medium text-ink-900">{t('retryDialog.all')}</div>
                        <div className="text-xs text-ink-500 mt-1">
                            {t('retryDialog.allDesc', { n: stats.totalContacts || 0, s: succ })}
                        </div>
                    </button>
                </div>
                <div className="px-6 py-3 border-t border-cream-200 flex justify-end">
                    <button onClick={onClose} className="text-sm text-ink-500 hover:text-ink-900 px-3 py-1.5">{t('common:actions.cancel')}</button>
                </div>
            </div>
        </div>
    );
}

// 依 schema field 渲染輸入框（text / select）
function FieldInput({ field, value, onChange }) {
    const { t } = useTranslation('outbound');
    const cls = "w-full px-3 py-2 rounded-lg border border-cream-300 bg-white text-sm text-ink-900 focus:border-accent-500 focus:outline-none";
    if (field.type === 'select' && Array.isArray(field.options)) {
        return (
            <select className={cls} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
                <option value="">{t('field.selectPlaceholder')}</option>
                {field.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    }
    return <input className={cls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={field.label} />;
}

// 電話清單編輯
function PhonesEditor({ phones, setPhones }) {
    const { t } = useTranslation('outbound');
    return (
        <div className="space-y-2">
            {phones.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-ink-400 w-6">#{i + 1}</span>
                    <input
                        className="flex-1 px-3 py-2 rounded-lg border border-cream-300 bg-white text-sm font-mono tabular-nums text-ink-900 focus:border-accent-500 focus:outline-none"
                        value={p} onChange={(e) => setPhones(ps => ps.map((x, idx) => idx === i ? e.target.value : x))}
                        placeholder="09xxxxxxxx" />
                    {phones.length > 1 && (
                        <button onClick={() => setPhones(ps => ps.filter((_, idx) => idx !== i))}
                            className="size-8 inline-flex items-center justify-center text-ink-400 hover:text-danger-500 hover:bg-cream-100 rounded-md transition">
                            <X size={14} />
                        </button>
                    )}
                </div>
            ))}
            <button onClick={() => setPhones(ps => [...ps, ''])}
                className="text-xs text-accent-700 hover:text-accent-900 inline-flex items-center gap-1 px-1 py-1">
                <Plus size={12} /> {t('phones.add')}
            </button>
        </div>
    );
}

function AddContactDialog({ hostId, campaignId, schema, onClose, onSaved }) {
    const { t } = useTranslation('outbound');
    const [form, setForm] = useState({});
    const [phones, setPhones] = useState(['']);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    // 空 schema 時(問卷 campaign 沒上傳 CSV)→ 用 fallback「姓名 name」一個 field
    // 一旦提交了第一筆 contact,後端會根據 variables 推 schema,下次打開 dialog 就會顯示完整欄位
    const effectiveFields = (schema.fields && schema.fields.length > 0)
        ? schema.fields
        : [{ key: 'name', label: t('addDialog.fallbackNameLabel'), type: 'string' }];

    const submit = async () => {
        const validPhones = phones.map(p => p.trim()).filter(Boolean);
        if (validPhones.length === 0) { setErr(t('addDialog.errNoPhone')); return; }
        setSaving(true); setErr(null);
        try {
            const variables = {};
            for (const f of effectiveFields) {
                const v = form[f.key];
                if (v != null && String(v).trim() !== '') variables[f.key] = v;
            }
            await campaignApi.addContact(hostId, campaignId, { variables, phones: validPhones });
            onSaved();
        } catch (e) {
            setErr(e.message || t('addDialog.errAddFail')); setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.3)] w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 bg-white">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">add contact</div>
                        <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">{t('addDialog.title')}</h2>
                    </div>
                    <button onClick={onClose} className="size-9 inline-flex items-center justify-center text-ink-500 hover:bg-cream-100 rounded-md transition"><X size={18} /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {effectiveFields.map(f => (
                        <div key={f.key}>
                            <label className="block text-xs font-medium text-ink-700 mb-1">{f.label}</label>
                            <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm(s => ({ ...s, [f.key]: v }))} />
                        </div>
                    ))}
                    <div>
                        <label className="block text-xs font-medium text-ink-700 mb-1">{t('addDialog.phoneLabel')} <span className="text-danger-500">*</span></label>
                        <PhonesEditor phones={phones} setPhones={setPhones} />
                    </div>
                    {(!schema.fields || schema.fields.length === 0) && (
                        <div className="text-[11px] text-ink-400 px-3 py-2 bg-cream-100/50 rounded">
                            {t('addDialog.firstHint')}
                        </div>
                    )}
                    {err && <div className="text-sm text-danger-500">{err}</div>}
                </div>
                <div className="px-6 py-3 border-t border-cream-200 flex justify-end gap-2 bg-white">
                    <button onClick={onClose} className="text-sm text-ink-500 hover:text-ink-900 px-3 py-1.5">{t('common:actions.cancel')}</button>
                    <Button variant="accent" size="md" onClick={submit} disabled={saving}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t('addDialog.add')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ContactDetailDialog({ contact: c, hostId, campaignId, windowConfig, maxRedials, schema, canEdit = true, canDelete = true, onClose, onChanged }) {
    const { t } = useTranslation('outbound');
    const statusLabel = {
        pending: t('contactStatusFull.pending'),
        in_progress: t('contactStatusFull.inProgress'),
        retry_pending: t('contactStatusFull.retryPending'),
        done_success: t('contactStatusFull.doneSuccess'),
        done_failed: t('contactStatusFull.doneFailed'),
    }[c.status] || c.status;

    const [mode, setMode] = useState('view');   // 'view' | 'edit'
    const [editForm, setEditForm] = useState(() => ({ ...(c.variables || {}) }));
    const [editPhones, setEditPhones] = useState(() => (c.phones || []).map(p => p.number));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // schema 沒涵蓋的 variable key 也要能編輯 → 用 contact 自己的 keys 補上
    const editFields = (() => {
        const byKey = {};
        for (const f of (schema?.fields || [])) byKey[f.key] = f;
        const keys = Object.keys(c.variables || {});
        return keys.map(k => byKey[k] || { key: k, label: k, type: 'text' });
    })();

    const doSave = async () => {
        const validPhones = editPhones.map(p => String(p).trim()).filter(Boolean);
        if (validPhones.length === 0) { setErr(t('contactDialog.errNoPhone')); return; }
        setBusy(true); setErr(null);
        try {
            await campaignApi.editContact(hostId, campaignId, c.id, { variables: editForm, phones: validPhones });
            onChanged(t('contactDialog.toastUpdated'));
        } catch (e) { setErr(e.message || t('contactDialog.errUpdateFail')); setBusy(false); }
    };
    const doDelete = async () => {
        if (!confirm(t('contactDialog.confirmDelete'))) return;
        setBusy(true); setErr(null);
        try {
            await campaignApi.deleteContact(hostId, campaignId, c.id);
            onChanged(t('contactDialog.toastDeleted'));
        } catch (e) { setErr(e.message || t('contactDialog.errDeleteFail')); setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.3)] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 bg-white">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">
                            contact · {c.id.slice(0, 12)}
                        </div>
                        <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">
                            {c.variables?.name || c.sourceRow?.['姓名'] || t('contactDialog.noName')}
                        </h2>
                    </div>
                    <button onClick={onClose} className="size-9 inline-flex items-center justify-center text-ink-500 hover:bg-cream-100 rounded-md transition">
                        <X size={18} />
                    </button>
                </div>

                {mode === 'edit' ? (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                            {editFields.map(f => (
                                <div key={f.key}>
                                    <label className="block text-xs font-medium text-ink-700 mb-1">{f.label}</label>
                                    <FieldInput field={f} value={editForm[f.key]} onChange={(v) => setEditForm(s => ({ ...s, [f.key]: v }))} />
                                </div>
                            ))}
                            <div>
                                <label className="block text-xs font-medium text-ink-700 mb-1">{t('contactDialog.phoneLabel')}</label>
                                <PhonesEditor phones={editPhones} setPhones={setEditPhones} />
                            </div>
                            {err && <div className="text-sm text-danger-500">{err}</div>}
                        </div>
                        <div className="px-6 py-3 border-t border-cream-200 flex justify-end gap-2 bg-white">
                            <button onClick={() => { setMode('view'); setErr(null); }} className="text-sm text-ink-500 hover:text-ink-900 px-3 py-1.5">{t('common:actions.cancel')}</button>
                            <Button variant="accent" size="md" onClick={doSave} disabled={busy}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t('common:actions.save')}
                            </Button>
                        </div>
                    </>
                ) : (
                <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {/* 狀態 */}
                    <DetailSection title={t('contactDialog.section.status')}>
                        <DetailRow label={t('contactDialog.row.status')} value={statusLabel} />
                        <DetailRow label={t('contactDialog.row.attempts')} value={`${redialsOf(c)} / ${maxRedials}`} />
                        <DetailRow label={t('contactDialog.row.currentPhone')} value={`${c.phones?.[c.currentPhoneIdx || 0]?.number || '—'} (${(c.currentPhoneIdx || 0) + 1}/${(c.phones || []).length})`} />
                        <DetailRow label={t('contactDialog.row.nextDial')} value={(() => {
                            if (!c.nextEligibleAt) return '—';
                            // 時段外的 nextEligibleAt 不會在當下撥 → 顯示下個撥打窗口（對齊列表欄位）
                            if (c.nextEligibleAt > Date.now() && !isInWindow(c.nextEligibleAt, windowConfig)) {
                                return nextWindowLabel(c.nextEligibleAt, windowConfig, t);
                            }
                            return new Date(c.nextEligibleAt).toLocaleString('zh-TW', { hour12: false });
                        })()} />
                        {c.lastError && <DetailRow label={t('contactDialog.row.lastError')} value={<span className="text-danger-500">{c.lastError}</span>} />}
                        {c.lastCallSessionId && <DetailRow label={t('contactDialog.row.lastCall')} value={<code className="font-mono text-[11px]">{c.lastCallSessionId}</code>} />}
                        {c.formSubmissionId && <DetailRow label={t('contactDialog.row.formResult')} value={<code className="font-mono text-[11px]">{c.formSubmissionId}</code>} />}
                    </DetailSection>

                    {/* 電話清單 */}
                    <DetailSection title={t('contactDialog.section.phones', { n: (c.phones || []).length })}>
                        {(c.phones || []).map((p, i) => (
                            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-cream-100 last:border-b-0">
                                <span className="font-mono text-[10px] text-ink-400 w-6">#{i + 1}</span>
                                <span className="font-mono text-sm text-accent-700 tabular-nums flex-1">{p.number}</span>
                                <span className="text-[11px] text-ink-500">
                                    {p.terminationType || (p.lastTriedAt ? t('contactDialog.phone.tried') : t('contactDialog.phone.notTried'))}
                                </span>
                                {p.lastTriedAt && (
                                    <span className="font-mono text-[10px] text-ink-400">
                                        {new Date(p.lastTriedAt).toLocaleString('zh-TW', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                )}
                            </div>
                        ))}
                    </DetailSection>

                    {/* 撥打歷程 — 每一次嘗試（含完全沒撥通的 NOANSWER/BUSY），最新在上 */}
                    <DetailSection title={t('contactDialog.section.attemptHistory')}>
                        {(c.attemptHistory && c.attemptHistory.length) ? (
                            [...c.attemptHistory].reverse().map((a, i) => {
                                const oc = {
                                    answered: t('detail.outcome.answered'), no_answer: t('detail.outcome.noAnswer'),
                                    busy: t('detail.outcome.busy'), rejected: t('outcomeBadge.hangImmediate'),
                                    voicemail: t('detail.outcome.voicemail'), no_reach: t('detail.outcome.noReach'),
                                    unknown: t('detail.outcome.sysHangup'),
                                }[a.outcome] || a.result || a.outcome || '—';
                                const fmt = (ms) => new Date(ms).toLocaleString('zh-TW', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
                                return (
                                    <div key={i} className="flex items-center gap-2 py-1.5 border-b border-cream-100 last:border-b-0">
                                        <span className="font-mono text-[10px] text-ink-400 w-24 shrink-0">{a.at ? fmt(a.at) : '—'}</span>
                                        <span className="font-mono text-xs text-accent-700 tabular-nums w-28 shrink-0">{a.phone || '—'}</span>
                                        <span className={['text-[11px] font-medium', a.wasAnswered ? 'text-accent-700' : 'text-ink-500'].join(' ')}>{oc}</span>
                                        {a.result && <span className="font-mono text-[10px] text-ink-400">{a.result}</span>}
                                        <div className="flex-1" />
                                        {a.nextDialAt && <span className="text-[10px] text-ink-400 shrink-0">{t('contactDialog.attempt.next')} {fmt(a.nextDialAt)}</span>}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-[11px] text-ink-400 py-1.5">{t('contactDialog.attempt.empty')}</div>
                        )}
                    </DetailSection>

                    {/* AI 變數 */}
                    <DetailSection title={t('contactDialog.section.vars')}>
                        {Object.entries(c.variables || {}).map(([k, v]) => (
                            <DetailRow key={k} label={<code className="font-mono text-[11px]">{`{{${k}}}`}</code>} value={String(v)} />
                        ))}
                    </DetailSection>

                    {/* 原始 Excel row */}
                    <DetailSection title={t('contactDialog.section.sourceRow')}>
                        {Object.entries(c.sourceRow || {}).map(([k, v]) => (
                            <DetailRow key={k} label={k} value={String(v || t('contactDialog.sourceEmpty'))} />
                        ))}
                    </DetailSection>
                    {err && <div className="text-sm text-danger-500">{err}</div>}
                </div>
                <div className="px-6 py-3 border-t border-cream-200 flex items-center gap-2 bg-white">
                    {canDelete && (
                        <button onClick={doDelete} disabled={busy}
                            className="text-sm text-danger-500 hover:text-danger-700 inline-flex items-center gap-1 px-3 py-1.5 disabled:opacity-50">
                            <Trash2 size={14} /> {t('common:actions.delete')}
                        </button>
                    )}
                    <div className="flex-1" />
                    <button onClick={onClose} className="text-sm text-ink-500 hover:text-ink-900 px-3 py-1.5">{t('common:actions.close')}</button>
                    {canEdit && (
                        <Button variant="ghost" size="md" onClick={() => { setEditForm({ ...(c.variables || {}) }); setEditPhones((c.phones || []).map(p => p.number)); setMode('edit'); }}>
                            <Pencil size={14} /> {t('common:actions.edit')}
                        </Button>
                    )}
                </div>
                </>
                )}
            </div>
        </div>
    );
}

function DetailSection({ title, children }) {
    return (
        <div>
            <div className="text-xs font-medium text-ink-700 mb-1.5">{title}</div>
            <div className="border border-cream-200 rounded-md bg-white px-3 py-1.5">{children}</div>
        </div>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="flex items-start gap-3 py-1.5 border-b border-cream-100 last:border-b-0">
            <div className="text-xs text-ink-500 w-44 shrink-0 break-all">{label}</div>
            <div className="text-sm text-ink-900 flex-1 min-w-0 break-all">{value || '—'}</div>
        </div>
    );
}
